import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface TemplatePreviewProps {
  type: string;
}

export const TemplatePreview = ({ type }: TemplatePreviewProps) => {
  const renderEmploymentPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-primary">ARBEITSVERTRAG</h1>
        <p className="text-sm text-muted-foreground">gemäß § 2 Nachweisgesetz (NachwG)</p>
      </div>

      <div className="space-y-4 text-sm">
        <div>
          <p className="font-semibold">Zwischen</p>
          <p className="pl-4 text-muted-foreground">[Firmenname]</p>
          <p className="pl-4 text-muted-foreground">[Firmenadresse]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitgeber" genannt -</p>
        </div>
        
        <div>
          <p className="font-semibold">und</p>
          <p className="pl-4 text-muted-foreground">[Name des Mitarbeiters]</p>
          <p className="pl-4 text-muted-foreground">[Adresse des Mitarbeiters]</p>
          <p className="pl-4 text-muted-foreground">geb. am [Geburtsdatum]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitnehmer" genannt -</p>
        </div>

        <Separator />

        <div className="space-y-3">
          <h3 className="font-semibold">§ 1 Beginn und Dauer des Arbeitsverhältnisses</h3>
          <p className="text-muted-foreground">(1) Das Arbeitsverhältnis beginnt am [Startdatum].</p>
          <p className="text-muted-foreground">(2) Das Arbeitsverhältnis wird auf unbestimmte Zeit geschlossen.</p>
          <p className="text-muted-foreground">(3) Die ersten [X] Monate gelten als Probezeit.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 2 Tätigkeit und Arbeitsort</h3>
          <p className="text-muted-foreground">(1) Der Arbeitnehmer wird als [Position] eingestellt.</p>
          <p className="text-muted-foreground">(2) Der Arbeitnehmer ist verpflichtet, auch andere zumutbare Arbeiten auszuführen.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 3 Arbeitszeit</h3>
          <p className="text-muted-foreground">(1) Die regelmäßige wöchentliche Arbeitszeit beträgt [X] Stunden.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 4 Vergütung</h3>
          <p className="text-muted-foreground">(1) Der Arbeitnehmer erhält eine monatliche Bruttovergütung in Höhe von [Gehalt] €.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 5 Urlaub</h3>
          <p className="text-muted-foreground">(1) Der Arbeitnehmer hat Anspruch auf [X] Arbeitstage Erholungsurlaub pro Kalenderjahr.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 6 - § 11 Weitere Paragraphen</h3>
          <p className="text-muted-foreground">• Krankheit und Arbeitsunfähigkeit</p>
          <p className="text-muted-foreground">• Kündigung</p>
          <p className="text-muted-foreground">• Nebentätigkeit</p>
          <p className="text-muted-foreground">• Verschwiegenheit</p>
          <p className="text-muted-foreground">• Datenschutz</p>
          <p className="text-muted-foreground">• Schlussbestimmungen</p>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-8 pt-4">
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitgeber</p>
          </div>
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitnehmer</p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Firmenname]</Badge>
          <Badge variant="outline">[Firmenadresse]</Badge>
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Adresse]</Badge>
          <Badge variant="outline">[Geburtsdatum]</Badge>
          <Badge variant="outline">[Position]</Badge>
          <Badge variant="outline">[Abteilung]</Badge>
          <Badge variant="outline">[Startdatum]</Badge>
          <Badge variant="outline">[Gehalt]</Badge>
          <Badge variant="outline">[Urlaubstage]</Badge>
          <Badge variant="outline">[Wochenstunden]</Badge>
        </div>
      </div>
    </div>
  );

  const renderAmendmentPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-primary">VERTRAGSÄNDERUNG</h1>
        <p className="text-sm text-muted-foreground">zum Arbeitsvertrag vom [Datum]</p>
      </div>

      <div className="space-y-4 text-sm">
        <div>
          <p className="font-semibold">Zwischen</p>
          <p className="pl-4 text-muted-foreground">[Firmenname]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitgeber" genannt -</p>
        </div>
        
        <div>
          <p className="font-semibold">und</p>
          <p className="pl-4 text-muted-foreground">[Name des Mitarbeiters]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitnehmer" genannt -</p>
        </div>

        <Separator />

        <p className="text-muted-foreground">
          Die Parteien vereinbaren folgende Änderungen zum bestehenden Arbeitsvertrag mit Wirkung zum [Datum]:
        </p>

        <div className="space-y-3">
          <h3 className="font-semibold">1. Positionsänderung</h3>
          <p className="text-muted-foreground">Bisherige Position: [Alte Position]</p>
          <p className="text-muted-foreground">Neue Position: [Neue Position]</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">2. Gehaltsanpassung</h3>
          <p className="text-muted-foreground">Bisheriges Gehalt: [Altes Gehalt] €</p>
          <p className="text-muted-foreground">Neues Gehalt: [Neues Gehalt] €</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">3. Begründung</h3>
          <p className="text-muted-foreground">[Grund der Änderung]</p>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-8 pt-4">
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitgeber</p>
          </div>
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitnehmer</p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Alte Position]</Badge>
          <Badge variant="outline">[Neue Position]</Badge>
          <Badge variant="outline">[Altes Gehalt]</Badge>
          <Badge variant="outline">[Neues Gehalt]</Badge>
          <Badge variant="outline">[Wirksamkeitsdatum]</Badge>
          <Badge variant="outline">[Begründung]</Badge>
        </div>
      </div>
    </div>
  );

  const renderCertificatePreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-4 py-8">
        <div className="border-4 border-primary/20 p-8 mx-auto max-w-md rounded-lg">
          <h1 className="text-3xl font-bold text-primary mb-4">ZERTIFIKAT</h1>
          <p className="text-lg text-muted-foreground mb-6">[Titel des Zertifikats]</p>
          
          <p className="text-muted-foreground mb-2">Hiermit wird bestätigt, dass</p>
          <p className="text-xl font-semibold mb-4">[Name des Mitarbeiters]</p>
          
          <p className="text-muted-foreground mb-6">[Inhalt des Zertifikats - z.B. Schulung erfolgreich abgeschlossen]</p>
          
          <div className="pt-8">
            <p className="text-sm text-muted-foreground">[Ort], den [Datum]</p>
            <div className="mt-8 border-t pt-2 mx-auto w-48">
              <p className="text-xs text-muted-foreground">Unterschrift / Stempel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Titel des Zertifikats]</Badge>
          <Badge variant="outline">[Inhalt]</Badge>
          <Badge variant="outline">[Ausstellungsdatum]</Badge>
          <Badge variant="outline">[Zertifikatstyp]</Badge>
        </div>
      </div>
    </div>
  );

  const renderAnniversaryPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-4 py-8">
        <div className="border-4 border-amber-500/30 p-8 mx-auto max-w-md rounded-lg bg-gradient-to-b from-amber-50/50 to-white dark:from-amber-900/10 dark:to-gray-900">
          <div className="text-amber-600 text-4xl mb-4">★</div>
          <h1 className="text-3xl font-bold text-amber-700 dark:text-amber-500 mb-2">JUBILÄUMSURKUNDE</h1>
          <p className="text-lg text-muted-foreground mb-6">[X] Jahre Betriebszugehörigkeit</p>
          
          <p className="text-muted-foreground mb-2">Wir gratulieren</p>
          <p className="text-2xl font-semibold text-amber-700 dark:text-amber-500 mb-4">[Name des Mitarbeiters]</p>
          
          <p className="text-muted-foreground mb-4">
            herzlich zum [X]-jährigen Firmenjubiläum am [Datum].
          </p>
          
          <p className="text-muted-foreground italic mb-6">
            "[Persönlicher Text - z.B. Wir danken für die langjährige Treue und das außerordentliche Engagement...]"
          </p>
          
          <div className="pt-8">
            <p className="text-sm text-muted-foreground">[Firmenname]</p>
            <div className="mt-8 border-t border-amber-300 pt-2 mx-auto w-48">
              <p className="text-xs text-muted-foreground">Geschäftsführung</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Jahre der Betriebszugehörigkeit]</Badge>
          <Badge variant="outline">[Jubiläumsdatum]</Badge>
          <Badge variant="outline">[Persönlicher Text]</Badge>
        </div>
      </div>
    </div>
  );

  const renderWarningPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="space-y-4 text-sm">
        <div className="text-right text-muted-foreground">
          <p>[Firmenname]</p>
          <p>[Firmenadresse]</p>
          <p>[Ort], den [Datum]</p>
        </div>

        <div className="mt-8">
          <p className="font-semibold">[Name des Mitarbeiters]</p>
          <p className="text-muted-foreground">[Adresse des Mitarbeiters]</p>
        </div>

        <div className="text-center py-6">
          <h1 className="text-2xl font-bold text-destructive">ABMAHNUNG</h1>
          <p className="text-sm text-muted-foreground">[X]. Abmahnung</p>
        </div>

        <p className="text-muted-foreground">Sehr geehrte(r) [Name],</p>

        <p className="text-muted-foreground">
          hiermit mahnen wir Sie wegen des folgenden Fehlverhaltens ab:
        </p>

        <div className="bg-destructive/10 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Grund der Abmahnung:</h3>
          <p className="text-muted-foreground">[Beschreibung des Fehlverhaltens]</p>
        </div>

        <div className="space-y-2">
          <h3 className="font-semibold">Sachverhalt:</h3>
          <p className="text-muted-foreground">[Detaillierte Beschreibung des Vorfalls]</p>
        </div>

        <div className="bg-amber-500/10 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Konsequenzen bei Wiederholung:</h3>
          <p className="text-muted-foreground">[Beschreibung der Konsequenzen - z.B. Kündigung]</p>
        </div>

        <p className="text-muted-foreground">
          Wir fordern Sie auf, Ihr Verhalten unverzüglich zu ändern und künftig derartiges Fehlverhalten zu unterlassen.
        </p>

        <Separator />

        <div className="grid grid-cols-1 gap-8 pt-4">
          <div>
            <p className="text-muted-foreground border-b pb-2 mb-2">Unterschrift Arbeitgeber</p>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg mt-8">
          <h4 className="font-semibold mb-2">Empfangsbestätigung:</h4>
          <p className="text-muted-foreground text-sm">
            Ich bestätige den Empfang dieser Abmahnung am _____________.
          </p>
          <div className="mt-4 border-t pt-2">
            <p className="text-xs text-muted-foreground">Unterschrift Arbeitnehmer</p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Abmahnungsnummer]</Badge>
          <Badge variant="outline">[Datum der Abmahnung]</Badge>
          <Badge variant="outline">[Grund der Abmahnung]</Badge>
          <Badge variant="outline">[Detaillierter Sachverhalt]</Badge>
          <Badge variant="outline">[Konsequenzen]</Badge>
        </div>
      </div>
    </div>
  );

  const renderVehicleAssignmentPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-primary">VEREINBARUNG ÜBER DIE ÜBERLASSUNG EINES DIENSTFAHRZEUGS</h1>
      </div>

      <div className="space-y-4 text-sm">
        <div>
          <p className="font-semibold">Zwischen der Firma</p>
          <p className="pl-4 text-muted-foreground">[Firmenname]</p>
          <p className="pl-4 text-muted-foreground">[Firmenadresse]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitgeber" genannt -</p>
        </div>
        
        <div>
          <p className="font-semibold">und</p>
          <p className="pl-4 text-muted-foreground">[Name des Mitarbeiters]</p>
          <p className="pl-4 text-muted-foreground">[Adresse des Mitarbeiters]</p>
          <p className="pl-4 italic text-muted-foreground">- nachfolgend "Arbeitnehmer" genannt -</p>
        </div>

        <Separator />

        <div className="space-y-3">
          <h3 className="font-semibold">§ 1 Überlassung eines Dienstfahrzeugs</h3>
          <p className="text-muted-foreground">(1) Der Arbeitgeber überlässt dem Arbeitnehmer ein Kraftfahrzeug der Marke [Fahrzeugbezeichnung], amtliches Kennzeichen [Kennzeichen], zur dienstlichen Nutzung.</p>
          <p className="text-muted-foreground">(2) Der Arbeitgeber ist berechtigt, dem Arbeitnehmer jederzeit ein gleichwertiges oder anderes Dienstfahrzeug zur Verfügung zu stellen.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 2 Nutzung des Dienstfahrzeugs</h3>
          <p className="text-muted-foreground">(1) Das Dienstfahrzeug darf ausschließlich vom Arbeitnehmer selbst geführt werden.</p>
          <p className="text-muted-foreground">(2) Eine private Nutzung des Dienstfahrzeugs ist ausdrücklich nicht gestattet.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">§ 3 - § 10 Weitere Paragraphen</h3>
          <p className="text-muted-foreground">• Kosten des Fahrzeugs</p>
          <p className="text-muted-foreground">• Pflichten des Arbeitnehmers</p>
          <p className="text-muted-foreground">• Unfälle, Verluste, Beschädigungen</p>
          <p className="text-muted-foreground">• Haftung des Arbeitnehmers</p>
          <p className="text-muted-foreground">• Rückgabe des Fahrzeugs</p>
          <p className="text-muted-foreground">• Entzug der Fahrerlaubnis</p>
          <p className="text-muted-foreground">• Nutzung durch Dritte</p>
          <p className="text-muted-foreground">• Schlussbestimmungen</p>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-8 pt-4">
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitgeber</p>
          </div>
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">[Ort, Datum]</p>
            <p className="text-xs text-muted-foreground mt-2">Unterschrift Arbeitnehmer</p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Firmenname]</Badge>
          <Badge variant="outline">[Firmenadresse]</Badge>
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Adresse]</Badge>
          <Badge variant="outline">[Fahrzeugbezeichnung]</Badge>
          <Badge variant="outline">[Kennzeichen]</Badge>
          <Badge variant="outline">[Gültig ab]</Badge>
        </div>
      </div>
    </div>
  );

  const renderSafetyInstructionPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-primary">SICHERHEITSUNTERWEISUNG</h1>
        <p className="text-sm text-muted-foreground">nach Arbeitsschutzgesetz (ArbSchG) und DGUV-Vorschriften</p>
        <p className="text-xs text-muted-foreground">für Arbeiten auf Baustellen und in technischen Anlagen</p>
      </div>

      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground">Unternehmen:</p>
            <p className="font-medium">[Firmenname]</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Datum:</p>
            <p className="font-medium">[Datum]</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Mitarbeiter:</p>
            <p className="font-medium">[Name des Mitarbeiters]</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Unterweisender:</p>
            <p className="font-medium">[Name des Unterweisenden]</p>
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <h3 className="font-semibold">1. Rechtliche Grundlage und Ziel der Unterweisung</h3>
          <p className="text-muted-foreground text-xs">Diese Sicherheitsunterweisung erfolgt auf Grundlage § 12 ArbSchG und DGUV Vorschrift 1.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">2. Verantwortung und Mitwirkungspflicht</h3>
          <p className="text-muted-foreground text-xs">Arbeitssicherheit ist keine freiwillige Leistung, sondern Pflicht für alle Beteiligten.</p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">3-14. Weitere Abschnitte</h3>
          <p className="text-muted-foreground text-xs">• Allgemeines Verhalten auf Baustellen</p>
          <p className="text-muted-foreground text-xs">• Persönliche Schutzausrüstung (PSA)</p>
          <p className="text-muted-foreground text-xs">• Stolper-, Rutsch- und Sturzgefahren</p>
          <p className="text-muted-foreground text-xs">• Arbeiten mit Leitern und in der Höhe</p>
          <p className="text-muted-foreground text-xs">• Elektrische Gefährdungen</p>
          <p className="text-muted-foreground text-xs">• Besondere Gefahren bei Glasfaserarbeiten</p>
          <p className="text-muted-foreground text-xs">• Werkzeuge, Maschinen und Arbeitsmittel</p>
          <p className="text-muted-foreground text-xs">• Ordnung, Sauberkeit und Brandschutz</p>
          <p className="text-muted-foreground text-xs">• Verhalten bei Unfällen und Notfällen</p>
          <p className="text-muted-foreground text-xs">• Arbeitsmedizinische Vorsorge</p>
          <p className="text-muted-foreground text-xs">• Konsequenzen bei Verstößen</p>
        </div>

        <Separator />

        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">Bestätigung der Unterweisung:</h4>
          <p className="text-muted-foreground text-sm">
            Ich bestätige, dass ich diese Sicherheitsunterweisung erhalten habe, den Inhalt verstanden habe und die Sicherheitsregeln einhalte.
          </p>
          <div className="mt-4 border-t pt-2">
            <p className="text-xs text-muted-foreground">Unterschrift Mitarbeiter</p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Firmenname]</Badge>
          <Badge variant="outline">[Name des Mitarbeiters]</Badge>
          <Badge variant="outline">[Name des Unterweisenden]</Badge>
          <Badge variant="outline">[Datum]</Badge>
          <Badge variant="outline">[Abteilung]</Badge>
        </div>
      </div>
    </div>
  );

  const renderKeyHandoverPreview = () => (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-primary">TRANSPONDER-/SCHLÜSSEL-ÜBERGABE</h1>
        <p className="text-sm text-muted-foreground">Übergabeprotokoll für Zugangsträger</p>
      </div>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <span className="text-muted-foreground">Personalnr.:</span><span className="font-medium">[Personalnummer]</span>
          <span className="text-muted-foreground">Name:</span><span className="font-medium">[Name des Mitarbeiters]</span>
          <span className="text-muted-foreground">Anschrift:</span><span className="font-medium">[Adresse]</span>
          <span className="text-muted-foreground">Schließzeiten:</span><span className="font-medium">[Schließzeiten]</span>
          <span className="text-muted-foreground">Alarm-PIN:</span><span className="font-medium">[PIN]</span>
        </div>
        <Separator />
        <h3 className="font-semibold">Transponder-/Schlüssel-Übergabe</h3>
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <span className="text-muted-foreground">[Art]:</span><span className="font-medium">[Nummer]</span>
        </div>
        <Separator />
        <h4 className="font-semibold">Zur Beachtung</h4>
        <ul className="list-disc list-inside text-muted-foreground space-y-1 text-xs">
          <li>Schlüsselverwahrung: sichere Aufbewahrung, keine Weitergabe, Verlustmeldung</li>
          <li>Rückgabe bei Ausscheiden / Niederlegung der Aufgaben</li>
          <li>Schlüsselverwaltung über den verantwortlichen Ansprechpartner</li>
        </ul>
        <Separator />
        <div className="grid grid-cols-2 gap-8 pt-4">
          <p className="text-muted-foreground">[Ort], den [Datum]</p>
          <div className="text-center">
            <p className="text-muted-foreground border-t pt-2">Unterschrift des Empfängers</p>
          </div>
        </div>
      </div>
      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">Verfügbare Platzhalter:</h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">[Personalnummer]</Badge>
          <Badge variant="outline">[Name]</Badge>
          <Badge variant="outline">[Adresse]</Badge>
          <Badge variant="outline">[Schließzeiten]</Badge>
          <Badge variant="outline">[Alarm-PIN]</Badge>
          <Badge variant="outline">[Art]</Badge>
          <Badge variant="outline">[Nummer]</Badge>
        </div>
      </div>
    </div>
  );

  switch (type) {
    case "employment":
      return renderEmploymentPreview();
    case "amendment":
      return renderAmendmentPreview();
    case "certificate":
      return renderCertificatePreview();
    case "anniversary":
      return renderAnniversaryPreview();
    case "warning":
      return renderWarningPreview();
    case "vehicle":
      return renderVehicleAssignmentPreview();
    case "safety":
      return renderSafetyInstructionPreview();
    case "key_handover":
      return renderKeyHandoverPreview();
    default:
      return <p className="text-muted-foreground">Keine Vorschau verfügbar</p>;
  }
};
